import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ZODIAC_INFO } from '../data/zodiacInfo';
import { getAuthToken } from '../utils/auth';

function slugifySign(name) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // sin tildes
    .replace(/\s+/g, '-');
}

const ORDER = [
  'capricornio', 'acuario', 'piscis', 'aries', 'tauro', 'geminis',
  'cancer', 'leo', 'virgo', 'libra', 'escorpio', 'sagitario',
];

const SignInfoIndex = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const handleOpen = (key) => {
    navigate(`/sign-info/${encodeURIComponent(slugifySign(key))}`);
  };

  const handleBackHome = () => {
    const token = getAuthToken();
    navigate(token ? '/home' : '/welcome', { replace: true });
  };

  return (
    <>
      <style>{`
        @keyframes sign-star-drift {
          from { background-position: 22px 18px, 86px 44px, center; }
          to { background-position: 22px 130px, 86px 218px, center; }
        }

        @keyframes sign-wheel-spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }

        @keyframes sign-glow-pulse {
          0%, 100% { opacity: 0.62; transform: scale(1); }
          50% { opacity: 0.92; transform: scale(1.015); }
        }

        #signInfoRoot::before {
          content: "";
          position: fixed;
          inset: -20%;
          z-index: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 18%, rgba(177,74,255,0.24) 0%, transparent 25%),
            radial-gradient(circle at 84% 58%, rgba(123,31,205,0.22) 0%, transparent 31%),
            radial-gradient(circle at 50% 106%, rgba(218,165,32,0.18) 0%, transparent 30%);
          filter: blur(2px);
        }

        #signInfoRoot::after {
          content: "";
          position: fixed;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,226,138,0.82) 0 1px, transparent 1.45px),
            radial-gradient(circle, rgba(255,255,255,0.5) 0 1px, transparent 1.35px),
            linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.55));
          background-size: 112px 112px, 174px 174px, 100% 100%;
          background-position: 22px 18px, 86px 44px, center;
          opacity: 0.58;
          animation: sign-star-drift 18s linear infinite;
        }

        #signInfoRoot .signTopGlow {
          animation: sign-glow-pulse 6s ease-in-out infinite;
        }

        #signInfoRoot .signGrid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
          align-items: stretch;
        }

        #signInfoRoot .signTile {
          width: 100%;
          min-width: 0;
          -webkit-tap-highlight-color: transparent;
        }

        #signInfoRoot .signTile:hover {
          transform: translateY(-3px);
          box-shadow: 0 18px 38px rgba(0,0,0,0.34), 0 0 28px rgba(184,63,255,0.22);
        }

        #signInfoRoot .signTile:active {
          transform: scale(0.985);
        }

        #signInfoRoot .signTile::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
          background:
            radial-gradient(circle at 22% 20%, rgba(255,216,118,0.12), transparent 22%),
            radial-gradient(circle at 80% 82%, rgba(188,69,255,0.16), transparent 34%);
          opacity: 0.72;
        }

        #signInfoRoot .signTile::after {
          content: "→";
          position: absolute;
          right: 18px;
          bottom: 13px;
          color: #FFD979;
          font-size: 1.35rem;
          font-weight: 900;
          text-shadow: 0 0 12px rgba(218,165,32,0.48);
        }

        #signInfoRoot .signEmojiBadge {
          position: relative;
          display: grid;
          place-items: center;
          width: 58px;
          height: 58px;
          margin: 0 auto 12px;
          border-radius: 50%;
          border: 1px solid rgba(255,218,116,0.72);
          background: radial-gradient(circle at 35% 24%, rgba(190,80,255,0.52), rgba(35,6,70,0.84) 64%, rgba(5,1,18,0.92) 100%);
          box-shadow: 0 0 22px rgba(190,80,255,0.38), inset 0 0 14px rgba(255,218,116,0.08);
        }

        @media (max-width: 720px) {
          #signInfoRoot .signGrid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
        }

        @media (max-width: 520px) {
          #signInfoRoot .signScreenShell {
            padding: 72px 18px 18px !important;
            align-items: center !important;
          }

          #signInfoRoot .signCard {
            padding: 18px 18px 18px !important;
            border-radius: 24px !important;
            transform: none !important;
          }

          #signInfoRoot .signTitle {
            font-size: clamp(1.74rem, 9.2vw, 2.42rem) !important;
            line-height: 0.94 !important;
          }

          #signInfoRoot .signSubtitle {
            font-size: 0.78rem !important;
            line-height: 1.22 !important;
            margin-bottom: 12px !important;
          }

          #signInfoRoot .signGrid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px;
          }

          #signInfoRoot .signTile {
            min-height: 82px !important;
            padding: 8px 7px 18px !important;
            border-radius: 15px !important;
          }

          #signInfoRoot .signEmojiBadge {
            width: 34px;
            height: 34px;
            margin-bottom: 4px;
          }

          #signInfoRoot .signEmoji {
            font-size: 19px !important;
          }

          #signInfoRoot .signName {
            font-size: 0.78rem !important;
            line-height: 1.05 !important;
          }

          #signInfoRoot .signDates {
            font-size: 0.64rem !important;
            line-height: 1.05 !important;
            margin-top: 2px !important;
          }

          #signInfoRoot .signTile::after {
            right: 50%;
            transform: translateX(50%);
            bottom: 3px;
            font-size: 0.94rem;
          }

          #signInfoRoot .topBackBtn {
            top: calc(4px + env(safe-area-inset-top, 0px)) !important;
            left: calc(18px + env(safe-area-inset-left, 0px)) !important;
            min-height: 38px !important;
            padding: 0 15px !important;
            font-size: 0.92rem !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          #signInfoRoot::after,
          #signInfoRoot .signTopGlow {
            animation: none !important;
          }
        }
      `}</style>

      <div
        id="signInfoRoot"
        style={{
          minHeight: '100svh',
          position: 'relative',
          isolation: 'isolate',
          overflowX: 'hidden',
          overflowY: 'auto',
          color: '#fff',
          background: '#05010F',
        }}
      >
        <button type="button" onClick={handleBackHome} className="topBackBtn" style={styles.topBackBtn}>
          <span aria-hidden="true">←</span>
          {t('actions.back')}
        </button>


        <div className="signTopGlow" aria-hidden="true" style={styles.topGlow} />

        <div className="signScreenShell" style={{ ...styles.container, position: 'relative', zIndex: 2 }}>
          <div className="signCard" style={styles.card}>
            <h2 className="signTitle" style={styles.title}>
              {t('signInfoIndex.titleLineOne')}<br />
              <span style={{ color: '#FFD979' }}>{t('signInfoIndex.titleLineTwo')}</span>
            </h2>
            <div aria-hidden="true" style={styles.titleDivider}>
              <span />
              <i>✦</i>
              <span />
            </div>
            <p className="signSubtitle" style={styles.subtitle}>
              <Trans
                i18nKey="signInfoIndex.subtitle"
                ns="common"
                components={{ strong: <strong /> }}
              />
            </p>

            <div className="signGrid" style={styles.grid}>
              {ORDER.map((key) => {
                const s = ZODIAC_INFO[key] || ZODIAC_INFO[String(key || '').normalize('NFC')];
                if (!s) return null;
                const translatedName = t(`signInfoIndex.signs.${key}.name`, { defaultValue: s.nombre });
                const translatedDates = t(`signInfoIndex.signs.${key}.dates`, { defaultValue: s.fechas });
                return (
                  <button
                    type="button"
                    key={key}
                    className="signTile"
                    style={styles.signTile}
                    onClick={() => handleOpen(key)}
                  >
                    <div className="signEmojiBadge" aria-hidden="true">
                      <div className="signEmoji" style={styles.emoji}>{s.emoji}</div>
                    </div>
                    <div className="signName" style={styles.signName}>{translatedName}</div>
                    <div className="signDates" style={styles.dates}>{translatedDates}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  container: {
    minHeight: '100svh',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '74px 30px 34px',
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: 980,
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(20,5,42,0.78), rgba(8,2,24,0.82))',
    color: '#FFF6D8',
    borderRadius: 34,
    padding: '56px 38px 34px',
    boxShadow: '0 24px 70px rgba(0,0,0,0.48), 0 0 34px rgba(171,54,255,0.24), inset 0 1px 0 rgba(255,255,255,0.06)',
    border: '1px solid rgba(232,95,255,0.46)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    transform: 'translateY(-18px)',
  },
  title: {
    margin: 0,
    textAlign: 'center',
    color: '#F9F3FF',
    fontWeight: 500,
    fontSize: 'clamp(2.55rem, 7vw, 4.35rem)',
    lineHeight: 0.98,
    letterSpacing: '-0.03em',
    fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
    textShadow: '0 0 26px rgba(218,165,32,0.16)',
  },
  titleDivider: {
    width: 'min(100%, 520px)',
    margin: '12px auto 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    color: '#FFD979',
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 680,
    margin: '0 auto 28px',
    color: 'rgba(255,255,255,0.84)',
    fontWeight: 500,
    fontSize: '1.08rem',
    lineHeight: 1.45,
  },
  grid: {
    // layout is controlled by CSS to ensure 2 columns on mobile (Android/iOS)
  },
  signTile: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: 164,
    background: 'linear-gradient(135deg, rgba(45,7,85,0.78), rgba(12,2,34,0.86))',
    border: '1px solid rgba(217,93,255,0.52)',
    borderRadius: 22,
    padding: '22px 14px 32px',
    cursor: 'pointer',
    textAlign: 'center',
    color: '#FFF6D8',
    boxShadow: '0 14px 34px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.05)',
    transition: 'transform 150ms ease, box-shadow 200ms ease, border-color 200ms ease',
  },
  emoji: {
    fontSize: 34,
    lineHeight: 1,
    color: '#FFD979',
    textShadow: '0 0 16px rgba(218,165,32,0.42)',
  },
  signName: {
    position: 'relative',
    zIndex: 2,
    fontWeight: 800,
    color: '#FFFFFF',
    fontSize: '1.08rem',
    lineHeight: 1.18,
  },
  dates: {
    position: 'relative',
    zIndex: 2,
    fontSize: '0.92rem',
    color: 'rgba(190,184,255,0.9)',
    marginTop: 5,
  },
  topGlow: {
    position: 'fixed',
    top: '-18%',
    left: '50%',
    width: 'min(86vw, 760px)',
    height: 'min(86vw, 760px)',
    transform: 'translateX(-50%)',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(174,65,255,0.08) 0%, rgba(218,165,32,0.04) 34%, transparent 68%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  topBackBtn: {
    position: 'fixed',
    top: 'calc(14px + env(safe-area-inset-top, 0px))',
    left: 'calc(24px + env(safe-area-inset-left, 0px))',
    zIndex: 20,
    minHeight: 44,
    padding: '0 19px',
    borderRadius: 999,
    border: '1px solid rgba(232,95,255,0.72)',
    background: 'rgba(14,4,31,0.72)',
    color: '#FFE08B',
    fontWeight: 900,
    fontSize: '0.95rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    boxShadow: '0 0 22px rgba(190,60,255,0.32), inset 0 0 18px rgba(218,165,32,0.08)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
};

export default SignInfoIndex;
